import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export interface GeocodeResult {
  lat: number;
  lng: number;
  /** False when the address could not be resolved to a verified point. */
  found: boolean;
  /** True when the point is not verified street-level accurate. */
  approximate: boolean;
  label: string;
  /** Precision class of the coordinate (rooftop is preferred). */
  precision: "rooftop" | "parcel" | "interpolated" | "approximate" | "none";
  /** Which upstream geocoder produced the point. */
  provider: string | null;
}

/**
 * Resolves a property address to coordinates for the parcel map.
 * Returns `found: false` rather than a guessed point, so a report never opens
 * on the wrong property.
 */
export const geocodeAddress = createServerFn({ method: "POST" })
  .validator((input: unknown) =>
    z.object({ address: z.string().min(3).max(300) }).parse(input),
  )
  .handler(async ({ data }): Promise<GeocodeResult> => {
    const { requireParcelSearchBudget } = await import("./public-rate-limit.server");
    await requireParcelSearchBudget("geocode", { charge: false });
    const { resolveAddress } = await import("./geocode.server");
    return resolveAddress(data.address);
  });

export interface ReverseGeocodeResult {
  address: string | null;
  provider: string | null;
  /** 'rooftop' when the address feature sits on the dropped pin. */
  precision: "rooftop" | "near" | null;
  /** Distance from the pin to the addressed feature, in feet. */
  distanceFeet: number | null;
}

/**
 * Street address for a dragged map pin. Free sources only (OpenStreetMap
 * reverse, then the US Census block lookup); returns nulls when neither can
 * name the location rather than guessing.
 */
export const reverseGeocodePoint = createServerFn({ method: "POST" })
  .validator((input: unknown) =>
    z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).parse(input),
  )
  .handler(async ({ data }): Promise<ReverseGeocodeResult> => {
    const { requireParcelSearchBudget } = await import("./public-rate-limit.server");
    await requireParcelSearchBudget("geocode", { charge: false });
    const { reverseGeocode } = await import("./geocode.server");
    const hit = await reverseGeocode(data.lat, data.lng);
    return {
      address: hit?.address ?? null,
      provider: hit?.provider ?? null,
      precision: hit?.precision ?? null,
      distanceFeet: hit?.distanceFeet ?? null,
    };
  });
